import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { useTranslation } from 'app/lang';

import { parseLocationPath } from '../../Navigation/utils';
import { matches, sports, tournaments, tournamentStages, tournamentUnions } from '../selectors/gridEntities';

import dictionary from '../PrematchPage.dictionary.json';

type LocationInfo = {
  sportName: string | null;
  unionName: string | null;
  tournamentName: string | null;
  stageName: string | null;
  matchName: string | null;
};

export const useMetaContent = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const location = useMemo(() => parseLocationPath(pathname), [pathname]);
  const prematchSports = useSelector(sports);
  const prematchUnions = useSelector(tournamentUnions);
  const prematchTournaments = useSelector(tournaments);
  const prematchStages = useSelector(tournamentStages);
  const prematchMatches = useSelector(matches);

  const locationInfo: LocationInfo  = {
    sportName: null,
    unionName: null,
    tournamentName: null,
    stageName: null,
    matchName: null,
  };

  if (location.sportId) {
    const currentSport = prematchSports?.get(location.sportId);
    const sportName = currentSport?.get('name');
    locationInfo.sportName = typeof sportName === 'string' ? sportName : null;
  }

  if (location.tournamentUnionId) {
    const currentUnion = prematchUnions?.get(location.tournamentUnionId);
    const unionName = currentUnion?.get('name');
    locationInfo.unionName = typeof unionName === 'string' ? unionName : null;
  }

  if (location.tournamentId) {
    const currentTournament = prematchTournaments?.get(location.tournamentId);
    const tournamentName = currentTournament?.get('name');
    locationInfo.tournamentName = typeof tournamentName === 'string' ? tournamentName : null;
  }

  if (location.tournamentStageId) {
    const currentStage = prematchStages?.get(location.tournamentStageId);
    const stageName = currentStage?.get('name');
    locationInfo.stageName = typeof stageName === 'string' ? stageName : null;
  }

  if (location.matchId) {
    const currentMatch = prematchMatches?.get(location.matchId);
    const matchName = currentMatch?.get('name');
    locationInfo.matchName = typeof matchName === 'string' ? matchName : null;
  }

  const getMetaContent = (info: LocationInfo, isTitle?: boolean) => {
    const { sportName, unionName, tournamentName, stageName, matchName } = info;

    if (info.matchName) {
      return t(
        isTitle ? dictionary.matchTitle : dictionary.matchDescription,
        { sportName, unionName, tournamentName, stageName, matchName },
      );
    }

    if (info.stageName) {
      return t(
        isTitle ? dictionary.stageTitle : dictionary.stageDescription,
        { sportName, unionName, tournamentName, stageName },
      );
    }

    if (info.tournamentName) {
      return t(
        isTitle ? dictionary.tournamentTitle : dictionary.tournamentDescription,
        { sportName, unionName, tournamentName },
      );
    }

    if (info.unionName) {
      return t(
        isTitle ? dictionary.unionTitle : dictionary.unionDescription,
        { sportName, unionName },
      );
    }

    if (info.sportName) {
      return t(
        isTitle ? dictionary.sportTitle : dictionary.sportDescription,
        { sportName },
      );
    }
  };

  return { locationInfo, getMetaContent };
};


